import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import {
  SecurityProcessMenu,
  MEDIUM_SECURITY_PAGES,
} from '../components/SecurityProcessMenu.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import { isOtpLockedOut } from '../lib/action-denied.js';
import infoIcon from '../assets/info-icon.svg';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

type CheckStatus = 'pass' | 'fail' | null;

/**
 * Accessible pass/fail radio pair, faithful to the export's `AccessibleRadio`.
 * Drives the per-question identity checks at the top of the Medium Security
 * screen.
 */
function PassFailRadios({
  name,
  value,
  onChange,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              onChange={() => onChange(option)}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** A labelled identity question with its full name / value and pass/fail radios. */
function IdentityQuestion({
  label,
  value,
  name,
  status,
  onChange,
}: {
  label: string;
  value: string;
  name: string;
  status: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          {label}
        </h3>
        <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
          {value}
        </p>
      </div>
      <div className="ml-auto mt-5">
        <PassFailRadios name={name} value={status} onChange={onChange} />
      </div>
    </div>
  );
}

interface OtpGenerateResult {
  sent: boolean;
  resultCode?: string;
  maskedMobile?: string;
  attemptsRemaining?: number;
  blocked?: boolean;
  message?: string;
}

interface OtpValidateResult {
  valid: boolean;
  resultCode?: string;
  message?: string;
  attemptsRemaining?: number;
  expired?: boolean;
  blocked?: boolean;
}

/**
 * The customer's identity-question data (`POST /api/security/questions`,
 * bank-gateway PascalCase) — only the mobile-number fields this screen needs to
 * display the number BEFORE the OTP is generated (CCF-16). `MobileNumber` is the
 * full number on file (what the Figma renders and the customer reads back);
 * `MaskedMobile` is the masked fallback used when a full number is not returned.
 */
interface SecurityQuestionsData {
  MobileNumber?: string;
  MaskedMobile?: string;
}

/** Maximum OTP validation attempts within the 5-minute window (CCF-16). */
const MAX_OTP_ATTEMPTS = 3;

/**
 * Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY` for display.
 * Falls back to "Not available" when the gateway did not return a DoB.
 */
function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not available';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

/**
 * SCRN 3 — Medium Security (CCF-4 / EDS).
 *
 * Issue #312: the orange "medium risk" title now reads "EDS: medium risk" (was
 * missing the EDS scheme prefix carried by every sibling security page's
 * heading — e.g. "Visa: medium risk", "EDS: high security").
 *
 * Faithful port of the customer's Figma "medium risk" design: the per-question
 * identity checks (Full name, Date of birth — each pass/fail) followed by the
 * mandatory **2FA OTP** step.
 *
 * The customer's mobile number is shown up front (CCF-16), fetched pre-OTP from
 * `POST /api/security/questions` so the agent can read it out / the customer can
 * confirm it before any code is sent.
 *
 * The OTP step is wired to the Teller-ID-gated BFF: "Generate OTP" posts to
 * `POST /api/security/otp/generate` (sends a code to the registered mobile) and
 * "Validate" posts the 6-digit code to `POST /api/security/otp/validate`
 * (mock code `123456` succeeds). On success the customer is marked
 * medium-verified in the customer context, which unlocks navigation to the
 * Customer Dashboard.
 *
 * The Teller ID entered on ID&V is reused from the customer context and sent on
 * both OTP calls so they remain correctly Teller-ID-gated.
 */
export function MediumSecurityScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { customer, updateCustomer } = useCustomer();
  // #637 (SCRN-03 / CCF-113) — the security-failure branch-referral advisory
  // copy is admin-editable scripting text (content-catalogue keys); it falls
  // back to the compiled-in defaults. Rendered as PLAIN TEXT only (no HTML).
  const securityFailHeading = useContent('security.medium.fail.heading');
  const securityFailNote = useContent('security.medium.fail.note');

  const [fullNameStatus, setFullNameStatus] = useState<CheckStatus>(null);
  const [dobStatus, setDobStatus] = useState<CheckStatus>(null);

  const [otpSent, setOtpSent] = useState(false);
  // The pre-OTP mobile lookup, KEYED to the customer/Teller context it was
  // fetched for. Keeping the keys in state (rather than a bare status flag) lets
  // us derive "is the loaded number for the CURRENT context" purely from props,
  // so a context change cannot leave a previous customer's `loaded` state usable
  // for even a single render (no fail-open window before the effect re-runs).
  //   - `loaded`  : a number was returned for `{customerNumber, tellerId}`.
  //   - `missing` : the lookup succeeded but no number is on file.
  //   - `error`   : the lookup failed (non-2xx / network) — distinct guidance.
  const [mobileLookup, setMobileLookup] = useState<{
    customerNumber: string;
    tellerId: string;
    mobile: string;
    status: 'loading' | 'loaded' | 'missing' | 'error';
  }>({ customerNumber: '', tellerId: '', mobile: '', status: 'loading' });
  const [generating, setGenerating] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'pass' | 'fail' | null>(null);
  // CCF-16 OTP lifecycle: remaining attempts, expiry, and the 24-hour block.
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] =
    useState(MAX_OTP_ATTEMPTS);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpBlocked, setOtpBlocked] = useState(false);
  // Non-blocking inline warning for a wrong (non-final) OTP code (issue #164,
  // per the @dylan219 UX rule #80/#82/#94): a gateway-calling action's failure
  // must surface the code + message INLINE beside the action button, not only
  // as a transient toast, and stay retryable.
  const [otpError, setOtpError] = useState<{
    code?: string;
    message: string;
    // Which action produced the error, so it renders directly under THAT
    // button (#782): 'generate' → under Send Code; 'validate' → under Confirm.
    source: 'generate' | 'validate';
  } | null>(null);
  // #810: the gateway's (BFF-mapped, user-friendly) message for the expired
  // (2004) state, rendered INLINE in the dedicated branch alert (and toasted)
  // instead of the screen's fixed copy when present. The terminal blocked
  // branch keeps its CCF-16 24-hour branch-referral copy — the ruled 2005 text
  // ("request a new OTP") would contradict the in-force generation block; the
  // mapped message still surfaces in the blocked toast.
  const [otpExpiredMessage, setOtpExpiredMessage] = useState<string | null>(
    null,
  );

  const verified = customer.securityLevel === 'medium';
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const dobDisplay = formatDob(customer.dob);
  // First name for the 2FA read-back script ("Thanks {name}, …"). The Figma
  // greets the customer by first name; fall back gracefully to a generic
  // greeting when no name is on the verified-customer context.
  const firstName = (customer.name ?? '').trim().split(/\s+/)[0] ?? '';

  // The lookup state only counts when it is keyed to the CURRENT context — a
  // stale `loaded` from a previous customer is ignored. Derived from props, so
  // it is correct on the very first render after a context change (the effect's
  // re-fetch runs afterwards).
  const mobileForContext =
    mobileLookup.customerNumber === customerNumber &&
    mobileLookup.tellerId === tellerId
      ? mobileLookup
      : null;
  const customerMobile =
    mobileForContext?.status === 'loaded' ? mobileForContext.mobile : '';
  // CCF-16: Generate OTP is allowed ONLY once the current customer's number has
  // loaded and been confirmed — never on stale/loading/missing/error state.
  const mobileReady = mobileForContext?.status === 'loaded';

  // Fail-closed on context change: when the customer/Teller context changes
  // while this screen stays mounted, reset ALL per-customer verification + OTP
  // state so a previous customer's Pass results / sent OTP can never carry over
  // to a new CIF (CCF-4/CCF-16: verification is bound to the verified customer).
  // The mobile lookup is already context-keyed (above); this covers the rest.
  const lastContext = React.useRef(`${customerNumber}|${tellerId}`);
  useEffect(() => {
    const key = `${customerNumber}|${tellerId}`;
    if (lastContext.current === key) return;
    lastContext.current = key;
    setFullNameStatus(null);
    setDobStatus(null);
    setOtpSent(false);
    setOtpCode('');
    setOtpStatus(null);
    setOtpAttemptsRemaining(MAX_OTP_ATTEMPTS);
    setOtpExpired(false);
    setOtpBlocked(false);
    setOtpError(null);
  }, [customerNumber, tellerId]);

  // CCF-4: medium security is complete ONLY when Name + DoB + 2FA all pass.
  const allChecksPass =
    fullNameStatus === 'pass' && dobStatus === 'pass' && otpStatus === 'pass';

  // The 2FA/OTP step has reached a TERMINAL outcome: it passed, or it failed
  // terminally (3 attempts exhausted → blocked). A code that was never sent, or
  // is still awaiting entry / a non-final wrong attempt, is NOT terminal.
  const twoFaComplete =
    otpStatus === 'pass' || otpStatus === 'fail' || otpBlocked;

  // #263: the FAIL verdict (badge + branch-referral) must not be PRESENTED until
  // ALL required security steps have been completed — every identity question
  // answered AND the mandatory 2FA step at a terminal outcome. Until then a
  // pending step is shown as in-progress, never as a premature Fail. The
  // underlying pass/fail logic is unchanged; only the presentation is gated.
  const allStepsComplete =
    fullNameStatus !== null && dobStatus !== null && twoFaComplete;
  // Present FAIL only once the whole flow is complete and it did not pass.
  const showFail = allStepsComplete && !allChecksPass;

  // Flip the customer to medium-verified once all three checks pass — in any
  // order (CCF-4: no fixed order). This unlocks navigation to the Dashboard.
  React.useEffect(() => {
    if (allChecksPass && !verified) {
      // #616 (supervisor CCRS-2946): this base Medium Security screen IS the EDS
      // flow (SCRN-03, CCF-4 / EDS; heading "EDS: medium risk" per #312), so the
      // banner must name it — "EDS Medium Security Verified" — for parity with EDS
      // High (already 'EDS' via security-variants.tsx) and the ECL/Visa/Mortgage
      // siblings. Setting the variant EXPLICITLY to 'EDS' (rather than the earlier
      // `undefined`) both labels the flow AND guarantees no STALE prior variant can
      // leak — the concern #427 originally cleared with `undefined`.
      updateCustomer({
        securityLevel: 'medium',
        customerVerification: 'Y',
        verificationVariant: 'EDS',
      });
      toast.success('Customer verified (medium security).');
    }
  }, [allChecksPass, verified, updateCustomer]);

  /** Teller-ID header param for the Teller-ID-gated banking calls (mandatory). */
  function bankHeaderParams(): { header: { 'X-Teller-Id': string } } {
    return { header: { 'X-Teller-Id': tellerId } };
  }

  // CCF-112 (#477): record the Medium Security screen's TERMINAL outcome
  // (Passed / Failed) as a Non-API business event in Call History. The Failed
  // outcome is a pure client-side screen state (no gateway call), so — mirroring
  // the email-template event (#434) — BOTH outcomes are fired best-effort here
  // from the client, exactly once per terminal outcome. A once-guard (keyed to
  // the customer + outcome) prevents a re-fire on re-render; it resets when the
  // customer/Teller context changes so a fresh customer on the same screen is
  // recorded again. Never breaks the flow on a history-write failure.
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422 bug class): the
  // passive reset effect above and this effect run in the SAME commit after a
  // context swap, so on that render `customerNumber` is already the NEW CIF
  // while `allChecksPass`/`showFail` still derive from the PREVIOUS customer's
  // (not-yet-cleared) status state — posting then would write a false Security
  // Result against the wrong customer. `mobileForContext` is derived purely from
  // props (the mobile lookup is keyed to `{customerNumber, tellerId}`), so it
  // flips to `null` on the very first render of a new context and stays null
  // until that customer's own lookup loads. A terminal outcome is unreachable
  // without the OTP step (which requires `mobileReady` → a loaded lookup for the
  // current context), so requiring `mobileForContext` here provably ties the
  // outcome to the customer in context and cannot fire on the stale render.
  const securityEventFiredRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (securityEventFiredRef.current === lastContext.current) return;
    if (!customerNumber || !mobileForContext) return;
    // Terminal Pass = all checks pass; terminal Fail = every step complete but
    // the flow did not pass (`showFail`). Neither fires while a step is pending.
    const outcome: 'Passed' | 'Failed' | null = allChecksPass
      ? 'Passed'
      : showFail
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = lastContext.current;
    void api
      .POST('/calls/security-event', {
        params: bankHeaderParams(),
        body: { customerNumber, securityLevel: 'medium', outcome },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // `bankHeaderParams` is a stable render helper closing over `tellerId`, which
    // is already a dep via `lastContext`/`customerNumber`; excluded to keep the
    // effect keyed to the terminal-outcome transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecksPass, showFail, customerNumber, mobileForContext]);

  function ensureContext(): boolean {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      toast.error('No verified customer in context. Complete ID&V first.');
      return false;
    }
    return true;
  }

  // CCF-16: display the customer's mobile number BEFORE the OTP is generated, so
  // the agent reads it out and the customer confirms it before the code is sent.
  // Sourced from the existing `/security/questions` route (the same identity
  // data the checks above mark Pass/Fail against) — no new gateway field. Prefer
  // the full `MobileNumber` (what the Figma renders and the customer reads back);
  // fall back to the masked form when a full number is not returned.
  //
  // The lookup result gates Generate OTP (see `mobileReady`): the code is never
  // sent until a number has loaded for the CURRENT context, so the agent cannot
  // skip the CCF-16 confirmation step (no fail-open). The state is keyed to the
  // `{customerNumber, tellerId}` it was fetched for, so a previous customer's
  // number can never linger, and a late response for a stale context is ignored.
  useEffect(() => {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      setMobileLookup({
        customerNumber,
        tellerId,
        mobile: '',
        status: 'error',
      });
      return;
    }
    setMobileLookup({
      customerNumber,
      tellerId,
      mobile: '',
      status: 'loading',
    });
    let cancelled = false;
    void (async () => {
      try {
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (!response.ok) {
          setMobileLookup({
            customerNumber,
            tellerId,
            mobile: '',
            status: 'error',
          });
          return;
        }
        const q = data as SecurityQuestionsData | undefined;
        const mobile = q?.MobileNumber || q?.MaskedMobile;
        setMobileLookup({
          customerNumber,
          tellerId,
          mobile: mobile ?? '',
          status: mobile ? 'loaded' : 'missing',
        });
      } catch {
        if (!cancelled)
          setMobileLookup({
            customerNumber,
            tellerId,
            mobile: '',
            status: 'error',
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  async function handleGenerateOtp(): Promise<void> {
    if (!ensureContext()) return;
    // CCF-16 fail-closed guard: never send a code unless the CURRENT customer's
    // number has loaded and been confirmed (defence-in-depth alongside the
    // disabled button — guards against a stale enabled state).
    if (!mobileReady) {
      toast.error('Confirm the customer mobile number before sending a code.');
      return;
    }
    const callContextKey = `${customerNumber}|${tellerId}`;
    setGenerating(true);
    try {
      const { data, error, response } = await api.POST(
        '/security/otp/generate',
        {
          params: bankHeaderParams(),
          body: { customerNumber },
        },
      );
      // Ignore a late response if the customer context changed mid-flight — its
      // result belongs to a previous customer and must not mutate this one.
      if (lastContext.current !== callContextKey) return;
      if (response.status === 403) {
        // #788 (CCF-16) — a terminal OTP lockout (#639, per-CIF, sticky) fails
        // closed with 403 `{ error: 'otp_locked_out' }`. That is NOT an
        // RBAC/teller denial: route it to the dedicated inline CCF-16 block
        // alert beside the Send Code button (#782 placement), never the generic
        // permissions toast. Any OTHER 403 keeps the RBAC/teller-ID message.
        if (isOtpLockedOut(response.status, error)) {
          setOtpBlocked(true);
          return;
        }
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { sent: false }) as OtpGenerateResult;
      if (body.sent) {
        setOtpSent(true);
        // Refresh the displayed number with the masked form the gateway sent to,
        // keyed to the current context (keeps display + toast consistent).
        if (body.maskedMobile) {
          setMobileLookup({
            customerNumber,
            tellerId,
            mobile: body.maskedMobile,
            status: 'loaded',
          });
        }
        setOtpStatus(null);
        setOtpCode('');
        setOtpExpired(false);
        setOtpBlocked(false);
        setOtpError(null);
        setOtpAttemptsRemaining(body.attemptsRemaining ?? MAX_OTP_ATTEMPTS);
        const sentTo = body.maskedMobile ?? customerMobile;
        toast.success(
          sentTo ? `Code sent to ${sentTo}` : 'One-time passcode sent',
        );
      } else if (body.blocked) {
        // CCF-16: blocked for 24 hours after 3 failed attempts.
        setOtpBlocked(true);
        toast.error(
          body.message ??
            'OTP generation is blocked for 24 hours after 3 failed attempts.',
        );
      } else {
        // Send Code (generate) failed — surface the gateway code + message
        // INLINE under the Send Code button (#782), not only as a toast, so the
        // agent sees it right where they clicked and can retry.
        const message = body.message ?? 'Could not send the one-time passcode.';
        setOtpError({ code: body.resultCode, message, source: 'generate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'generate' });
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidateOtp(): Promise<void> {
    if (!ensureContext()) return;
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    const callContextKey = `${customerNumber}|${tellerId}`;
    setValidating(true);
    // Clear any prior inline warning at the start of a fresh validate attempt.
    setOtpError(null);
    try {
      const { data, response } = await api.POST('/security/otp/validate', {
        params: bankHeaderParams(),
        body: { customerNumber, otp: otpCode },
      });
      // Ignore a late response if the customer context changed mid-flight.
      if (lastContext.current !== callContextKey) return;
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { valid: false }) as OtpValidateResult;
      if (body.valid) {
        // 2FA passed. Medium-verification is gated on Name + DoB too (CCF-4),
        // handled by the effect watching all three checks.
        setOtpStatus('pass');
        setOtpError(null);
        toast.success('Two-factor authentication passed.');
        return;
      }

      if (typeof body.attemptsRemaining === 'number') {
        setOtpAttemptsRemaining(body.attemptsRemaining);
      }
      if (body.expired) {
        // CCF-16: 5-minute window elapsed — regenerate to get a new code.
        // Retryable, NOT a security failure: do not latch otpStatus to 'fail'.
        // The expired branch renders its own dedicated inline alert — clear the
        // generic wrong-code warning so they don't double up.
        // #810: prefer the gateway's (BFF-mapped, ruled #810) expired message
        // ("Your OTP has expired. Please request a new OTP…") over the fixed copy.
        const expiredMessage =
          body.message ??
          'The one-time passcode has expired. Generate a new code.';
        setOtpExpired(true);
        setOtpSent(false);
        setOtpError(null);
        setOtpExpiredMessage(expiredMessage);
        toast.error(expiredMessage);
      } else if (body.blocked) {
        // CCF-16: 3rd failure — OTP invalidated; generation blocked 24 hours.
        // Only NOW does 2FA fail terminally (drives the branch referral). The
        // blocked branch has its own dedicated inline alert — clear the generic
        // wrong-code warning so they don't double up.
        setOtpStatus('fail');
        setOtpBlocked(true);
        setOtpSent(false);
        setOtpError(null);
        toast.error(
          body.message ??
            'Incorrect code. OTP generation is now blocked for 24 hours.',
        );
      } else {
        // A non-final wrong code (attempt 1 or 2): stay retryable — do NOT set
        // otpStatus to 'fail', which would prematurely trigger the branch
        // referral (CCF-16: 3 attempts within the 5-minute window). Surface the
        // gateway code + message INLINE beside the Validate button as a
        // non-blocking warning (issue #164, per #80/#82/#94), KEEPING the toast.
        const remaining = body.attemptsRemaining;
        const message =
          body.message ??
          (typeof remaining === 'number'
            ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
            : 'The code does not match. Please retry.');
        setOtpError({ code: body.resultCode, message, source: 'validate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'validate' });
      toast.error(message);
    } finally {
      setValidating(false);
    }
  }

  // #517: EDS Medium (SCRN-03) entry from the shared medium pages map — used to
  // render the inline "Go to EDS High Security" button when mobile is on file.
  const edsHighEntry = MEDIUM_SECURITY_PAGES.find((p) => p.id === 'SCRN-03');

  return (
    <main
      className="max-w-[800px] pl-12 pr-12 py-12"
      aria-labelledby="medium-heading"
    >
      <div className="mb-6 flex items-center justify-between gap-8 flex-wrap">
        <h1
          id="medium-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#FF703E] m-0"
        >
          EDS: medium risk
        </h1>
        {/* Process-menu navigation (CCF-4 / issue #101): jump to a related
            process. Per @dylan219's product decision on #101, the menu offers
            THIS page's own relevant high security page — EDS High (the reported
            bug was that "High Risk Security" wrongly went to VISA High) — plus
            the OTHER medium security pages (VISA / Business 24 / ECL Medium).
            The consistent servicing entries ("Hold Information" → Account Holds,
            "Customer Notes" → Customer Notes) are appended by SecurityProcessMenu
            itself so every security page offers them (issue #222 part 3). Each
            entry targets an existing RBAC-gated route — the menu never bypasses
            deny-by-default; the router re-checks the screen permission on
            arrival and servicing screens re-gate on verification. */}
        <SecurityProcessMenu currentId="SCRN-03" />
      </div>

      <div className="space-y-6">
        {/* Medium Security verification section — the identity questions
            (Full name + Date of birth). Per the approved Figma (#566), the 2FA
            step is a SEPARATE section below this panel, not part of it. */}
        <InfoBox>
          <div className="space-y-6">
            {/* Identity questions */}
            <div className="space-y-6">
              <IdentityQuestion
                label="Full name:"
                value={customer.name ?? '—'}
                name="full-name"
                status={fullNameStatus}
                onChange={(v) => {
                  setFullNameStatus(v);
                }}
              />
              <div className="border-t-2 border-[#dad7d3]" />
              <IdentityQuestion
                label="Date of birth:"
                value={dobDisplay}
                name="dob"
                status={dobStatus}
                onChange={(v) => {
                  setDobStatus(v);
                }}
              />
            </div>
          </div>
        </InfoBox>

        {/* 2FA OTP step — a SEPARATE, clearly-delineated section AFTER the
            Medium Security verification section (approved Figma, #566). The
            customer's mobile number is shown ABOVE the Generate OTP button and
            BEFORE any code is sent (CCF-16: the system displays the customer's
            mobile phone number — Happy Path step 1 — so the agent reads it out
            and the customer confirms it before the code is sent), matching the
            Figma "Customer mobile number:" label and the ECL Medium sibling. It
            is fetched pre-OTP from `/security/questions` (full `MobileNumber`,
            falling back to the masked form). */}
        <InfoBox>
          <div className="space-y-6">
            <div>
              {/* Read-back scripting (approved Figma, #566): greet the customer
                  by first name, explain the extra security check, and ask for
                  their number before sending the code. */}
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                {firstName
                  ? `Thanks ${firstName}, I need to do one more security check. I'll send a code to your mobile phone.`
                  : `Thanks, I need to do one more security check. I'll send a code to your mobile phone.`}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                Can you tell me your phone number please?
              </p>
              <div className="flex items-baseline gap-2 mb-4">
                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                  Customer mobile number:
                </h3>
                {customerMobile && (
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                    {customerMobile}
                  </p>
                )}
                {mobileForContext?.status === 'loading' && (
                  <p
                    role="status"
                    className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                  >
                    Loading…
                  </p>
                )}
              </div>

              {/* CCF-16: 2FA cannot proceed without a number to confirm — show
                  guidance rather than fail open by sending a code regardless.
                  Distinguish "no number on file" (route to another verification)
                  from a technical lookup failure (retry / escalate). */}
              {mobileForContext?.status === 'missing' && (
                <p
                  role="alert"
                  className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] mt-0 mb-4"
                >
                  No mobile number is on file for this customer, so 2FA cannot
                  be completed. Use an alternative high-security verification.
                </p>
              )}
              {mobileForContext?.status === 'error' && (
                <p
                  role="alert"
                  className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] mt-0 mb-4"
                >
                  Unable to retrieve the customer&apos;s mobile number. Retry,
                  or escalate if the problem persists.
                </p>
              )}

              <div className="mt-4">
                <Button
                  type="button"
                  onClick={() => void handleGenerateOtp()}
                  disabled={
                    generating ||
                    otpStatus === 'pass' ||
                    otpBlocked ||
                    // CCF-16: do not let the agent send a code until the CURRENT
                    // customer's number has loaded and been confirmed (no
                    // fail-open on stale/loading/missing/error state).
                    !mobileReady
                  }
                  aria-label="Generate one-time passcode"
                  className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                >
                  {generating
                    ? 'Generating…'
                    : otpSent
                      ? 'Resend OTP'
                      : 'Send Code'}
                </Button>
              </div>

              {/* Send Code (generate) failure — gateway code + message shown
                  INLINE directly under the Send Code button (#782), so the
                  error is obvious right where the agent clicked. Validate
                  failures render under the Confirm button instead (below). */}
              {otpError &&
                otpError.source === 'generate' &&
                otpStatus !== 'pass' &&
                !otpExpired &&
                !otpBlocked && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `(${otpError.code}) ${otpError.message}`
                      : otpError.message}
                  </p>
                )}

              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-4 mb-4">
                I just sent the code to your phone. When you get it, can you
                read it back to me?
              </p>

              <label
                htmlFor="otp-code"
                className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
              >
                Verification number
              </label>

              <div className="flex items-center gap-4 flex-wrap">
                <input
                  id="otp-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="Enter verification number"
                  value={otpCode}
                  disabled={!otpSent || otpStatus === 'pass'}
                  onChange={(e) =>
                    setOtpCode(
                      e.target.value.replace(/[^0-9]/g, '').slice(0, 6),
                    )
                  }
                  style={{ width: '220px' }}
                  className="px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]"
                />

                <Button
                  type="button"
                  onClick={() => void handleValidateOtp()}
                  disabled={
                    !otpSent ||
                    otpCode.length !== 6 ||
                    validating ||
                    otpStatus === 'pass'
                  }
                  aria-label="Validate one-time passcode"
                  className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
                >
                  {validating ? 'Validating…' : 'Confirm'}
                </Button>

                {/* Overall security outcome (CCF-4): the flag reflects the WHOLE
                    verification — PASS only when Name + DoB + 2FA all pass, and
                    FAIL if any Pass/Fail question fails or 2FA fails. It must not
                    represent the 2FA result alone. */}
                {allChecksPass && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                    aria-label="Verification passed"
                  >
                    PASS
                  </span>
                )}
                {/* #263: only presented once every step (incl. 2FA) is done. */}
                {showFail && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                    aria-label="Verification failed"
                  >
                    FAIL
                  </span>
                )}
              </div>

              {/* Validate (Confirm) failure — a wrong (non-final) code: gateway
                  code + message shown INLINE directly under the Confirm button
                  (#782), where the agent just clicked, so it is obvious. Stays
                  retryable (input + Confirm stay enabled). The expired / blocked
                  branches render their own dedicated alerts below. */}
              {otpError &&
                otpError.source === 'validate' &&
                otpStatus !== 'pass' &&
                !otpExpired &&
                !otpBlocked && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `Incorrect code (${otpError.code}): ${otpError.message}`
                      : otpError.message}
                  </p>
                )}

              {/* OTP attempt/expiry/block status (CCF-16). */}
              {otpSent &&
                !otpBlocked &&
                !otpExpired &&
                otpStatus !== 'pass' && (
                  <p
                    role="status"
                    aria-live="polite"
                    className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-3 mb-0"
                  >
                    {`OTP attempts remaining: ${otpAttemptsRemaining}`}
                  </p>
                )}
              {otpExpired && (
                <p
                  role="status"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  {otpExpiredMessage ??
                    'The one-time passcode has expired. Generate a new code to continue.'}
                </p>
              )}
              {otpBlocked && (
                <p
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  The one-time passcode was entered incorrectly 3 times. OTP
                  generation is blocked for 24 hours — advise the customer to
                  visit a branch and add a customer note.
                </p>
              )}
            </div>
          </div>
        </InfoBox>

        {/* #517: when a mobile number IS on file, surface a direct "Go to EDS
            High Security" option so the agent can upgrade the verification level
            without hunting through the process-menu dropdown. Follows the same
            `mediumHighTransition` pattern used on VISA/BUS24 Medium in
            SecurityScreen.tsx.

            #675 (CCRS-3263): also surface the SAME "Proceed to high security?"
            box when NO mobile number is on file. In that case 2FA-by-OTP cannot
            be completed at all, so the agent needs a direct, actionable route to
            EDS High Security (the fallback verification path) — the red alert
            above tells them WHY, this box gives them HOW. This is additive to
            #517 (mobile-present case) and does not change what verification the
            medium flow requires. */}
        {(mobileReady || mobileForContext?.status === 'missing') &&
          edsHighEntry && (
            <div className="rounded-md border-2 border-[#dad7d3] bg-[#faf9f7] p-4">
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-0 mb-2">
                Proceed to high security?
              </p>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-3">
                If higher-level verification is required, transition to{' '}
                {edsHighEntry.highLabel}.
              </p>
              <Button
                type="button"
                onClick={() => navigate(edsHighEntry.highRoute)}
                aria-label={`Go to ${edsHighEntry.highLabel}`}
                className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
              >
                Go to {edsHighEntry.highLabel}
              </Button>
            </div>
          )}

        {/* Branch-referral guidance once verification has FAILED. #263: only
            presented after all steps (incl. 2FA) are complete, not on the first
            failed answer while 2FA is still outstanding. */}
        {showFail && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4 whitespace-pre-line">
                {securityFailHeading}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                {securityFailNote}
              </p>
            </div>
          </div>
        )}

        {/* Continue to Dashboard once medium-verified. */}
        {verified && (
          <div className="flex">
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              aria-label="Continue to dashboard"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Continue to dashboard
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
